"use client";

import React from "react";
import { ArrowLeft, Calendar, Clock, Share2, Tag, Bookmark } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import type { Post } from "@/integrations/wordpress/types";

declare const wvcClient: {
  getCurrentWPQueryParams: () => Record<string, any>;
};

function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="h-6 w-32 animate-pulse rounded-md bg-muted" />
        <div className="mt-6 h-12 w-3/4 animate-pulse rounded-md bg-muted" />
        <div className="mt-4 flex gap-4">
          <div className="size-10 animate-pulse rounded-full bg-muted" />
          <div className="space-y-2">
            <div className="h-4 w-28 animate-pulse rounded-md bg-muted" />
            <div className="h-3 w-20 animate-pulse rounded-md bg-muted" />
          </div>
        </div>
        <div className="mt-8 aspect-video w-full animate-pulse rounded-2xl bg-muted" />
        <div className="mt-10 space-y-4">
          <div className="h-4 w-full animate-pulse rounded-md bg-muted" />
          <div className="h-4 w-5/6 animate-pulse rounded-md bg-muted" />
          <div className="h-4 w-4/6 animate-pulse rounded-md bg-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <h2 className="text-xl font-semibold text-foreground">Unable to display post</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          There was an issue fetching this article.
        </p>
        <div className="mt-6">
          <Button variant="outline" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const currentPost: Post | undefined = posts[0];

  const title = currentPost?.title || "Essential kitchen remodeling trends for Brookhaven homes";
  const featuredImg = currentPost?.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_renovation_luxury_brookhaven.png";
  const postDate = currentPost?.date ? new Date(currentPost.date).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }) : "October 24, 2024";

  const authorName = currentPost?.author?.name || "Emily Johnson";
  const authorAvatar = currentPost?.author?.avatar?.["96"] || "";
  const authorInitials = authorName.split(" ").map((n) => n[0]).join("").slice(0, 2);

  const fallbackCategories = ["Kitchen Remodeling", "Design Tips", "Brookhaven Living"];
  const displayCategories = currentPost?.categories && currentPost.categories.length > 0
    ? currentPost.categories.map((c) => c.name)
    : fallbackCategories;

  const fallbackRelated: Array<{ id: string; title: string; category: string; link: string; readTime: string }> = [
    {
      id: "rel-1",
      title: "How to budget for a luxury master bathroom remodel in Atlanta",
      category: "Bathroom Remodeling",
      link: "/BlogPage",
      readTime: "5 min read",
    },
    {
      id: "rel-2",
      title: "Whole-home renovation checklist: what to know before permitting",
      category: "Planning & Permits",
      link: "/BlogPage",
      readTime: "7 min read",
    },
    {
      id: "rel-3",
      title: "Designing outdoor living spaces suited for Georgia summers",
      category: "Outdoor Living",
      link: "/BlogPage",
      readTime: "4 min read",
    },
  ];

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
      {/* Back to Blog Navigation */}
      <div className="mb-8">
        <Link
          to="/BlogPage"
          className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to all articles
        </Link>
      </div>

      {/* Article Header */}
      <header className="space-y-6">
        {/* Categories / Tags */}
        <div className="flex flex-wrap items-center gap-2">
          {displayCategories.map((cat, idx) => (
            <span
              key={idx}
              data-index={idx}
              className="inline-flex items-center gap-1 rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground"
            >
              <Tag className="size-3" />
              {cat}
            </span>
          ))}
        </div>

        {/* Primary Post Title */}
        <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:leading-tight">
          {title}
        </h1>

        {/* Post Metadata & Author Row */}
        <div className="flex flex-wrap items-center justify-between gap-4 py-2">
          <div className="flex items-center gap-3">
            <Avatar className="size-11 border border-border">
              {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
              <AvatarFallback className="bg-primary/20 text-xs font-semibold text-foreground">
                {authorInitials}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-sm font-semibold text-foreground">{authorName}</p>
              <p className="text-xs text-muted-foreground">Lead Designer at Brookhaven Elevation</p>
            </div>
          </div>

          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Calendar className="size-3.5" />
              {postDate}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="size-3.5" />
              6 min read
            </span>
          </div>
        </div>
      </header>

      {/* Featured Image Above Main Content */}
      <div className="my-8 overflow-hidden rounded-2xl border border-border shadow-md">
        <img
          src={featuredImg}
          alt={title}
          loading="lazy"
          decoding="async"
          className="aspect-16/9 w-full object-cover sm:aspect-21/9"
        />
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
        <article className="space-y-6 lg:col-span-8">
          {currentPost?.content ? (
            <div
              className="prose max-w-none text-base leading-relaxed text-foreground [&>h2]:font-serif [&>h2]:text-2xl [&>h2]:font-semibold [&>h2]:text-foreground [&>h2]:mt-8 [&>h2]:mb-4 [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:text-foreground [&>p]:mb-5 [&>p]:leading-relaxed [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>li]:mb-2"
              dangerouslySetInnerHTML={{ __html: currentPost.content }}
            />
          ) : (
            <div className="space-y-6 text-base leading-relaxed text-foreground">
              <p className="text-lg font-medium leading-relaxed text-foreground/90">
                Planning a kitchen renovation in Brookhaven requires balancing timeless architectural
                details with the functional demands of modern households. From cabinet layouts to high-performance
                appliances, every selection impacts your home&apos;s daily flow and long-term resale value.
              </p>

              <h2 className="font-serif text-2xl font-semibold text-foreground">
                Optimized layouts for entertaining and family life
              </h2>
              <p className="text-muted-foreground">
                Modern Brookhaven homes favor wide, multi-purpose kitchen islands that double as prep surfaces
                and casual gathering spots. When creating layout plans, prioritizing generous pathways (at least 42 to 48
                inches) ensures multiple cooks and guests can move comfortably without crowding primary work zones.
              </p>

              <h2 className="font-serif text-2xl font-semibold text-foreground">
                Durable surfaces with artisanal character
              </h2>
              <p className="text-muted-foreground">
                Engineered quartz surfaces and honed natural quartzite continue to dominate local projects for their
                resistance to staining and heat. Paired with custom inset cabinetry and warm brass hardware, these
                finishes reflect Brookhaven&apos;s signature transitional aesthetic.
              </p>

              <div className="rounded-xl border border-border bg-card p-6 shadow-xs">
                <h3 className="text-base font-semibold text-card-foreground">
                  Key planning takeaway
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Allow 6 to 8 weeks for detailed architectural planning and permitting before demo begins. This ensures
                  custom materials arrive on schedule and eliminates costly mid-construction delays.
                </p>
              </div>

              <h2 className="font-serif text-2xl font-semibold text-foreground">
                Smart storage and hidden pantries
              </h2>
              <p className="text-muted-foreground">
                Walk-in sculleries and appliance garages keep countertops clutter-free while keeping essential
                coffee stations and prep tools accessible. Designing dedicated electrical outlets inside cabinetry
                remains one of the most requested features among Brookhaven clients.
              </p>
            </div>
          )}

          <Separator className="my-8" />

          {/* Social / Action Strip */}
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-accent/40 p-4">
            <div className="text-sm font-medium text-foreground">
              Helpful renovation guides delivered by local professionals.
            </div>
            <div className="flex items-center gap-2">
              <Link to="/ContactRequestAnEstimatePage">
                <Button size="sm" variant="default">
                  Request an estimate
                </Button>
              </Link>
            </div>
          </div>

          {/* Author Card Box */}
          <Card className="mt-8 border border-border bg-card shadow-sm">
            <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-start">
              <Avatar className="size-16 shrink-0 border border-border">
                {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
                <AvatarFallback className="bg-primary/20 text-base font-semibold text-foreground">
                  {authorInitials}
                </AvatarFallback>
              </Avatar>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-serif text-lg font-semibold text-card-foreground">
                    Written by {authorName}
                  </h3>
                  <span className="text-xs font-medium text-muted-foreground">
                    Lead Designer
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Emily Johnson specializes in residential design across Brookhaven, Buckhead, and Dunwoody.
                  She works with homeowners to turn complex remodels into cohesive, durable, and conversion-ready spaces.
                </p>
                <div className="pt-1">
                  <Link
                    to="#"
                    className="text-xs font-semibold text-foreground underline underline-offset-4 hover:text-primary"
                  >
                    Learn more about our team and process →
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </article>

        {/* Sidebar / Recommended Content */}
        <aside className="space-y-8 lg:col-span-4">
          {/* Consultation Mini Card */}
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
            <h3 className="font-serif text-lg font-semibold text-card-foreground">
              Planning a renovation in Brookhaven?
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Speak directly with our builders. We provide transparent estimates and turnkey management.
            </p>
            <div className="mt-5 space-y-2">
              <Link to="/ContactRequestAnEstimatePage" className="block w-full">
                <Button className="w-full" variant="default">
                  Book a consultation
                </Button>
              </Link>
              <a
                href="tel:+14708479076"
                className="block text-center text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                Call (470) 847-9076
              </a>
            </div>
          </div>

          {/* Related Articles Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-lg font-semibold text-foreground">
                Recommended reading
              </h3>
              <Link to="/BlogPage" className="text-xs font-medium text-muted-foreground hover:text-foreground">
                View all
              </Link>
            </div>

            <div className="space-y-3">
              {fallbackRelated.map((item, idx) => (
                <div
                  key={item.id}
                  data-index={idx}
                  className="group rounded-xl border border-border bg-card p-4 transition-[border-color,box-shadow] hover:border-primary/50 hover:shadow-sm"
                >
                  <span className="text-[11px] font-medium tracking-wide text-primary">
                    {item.category}
                  </span>
                  <h4 className="mt-1 text-sm font-semibold text-card-foreground transition-colors group-hover:text-primary">
                    <Link to={item.link}>{item.title}</Link>
                  </h4>
                  <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{item.readTime}</span>
                    <Link
                      to={item.link}
                      className="font-medium text-foreground underline-offset-4 hover:underline"
                    >
                      Read article
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default function Article() {
  const currentWPQueryParams = typeof window !== "undefined" && typeof wvcClient !== "undefined"
    ? wvcClient.getCurrentWPQueryParams()
    : { post_type: "post", posts_per_page: 1 };

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section
      id="single-post-template-section"
      data-nav="light"
      className="bg-background text-foreground"
    >
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}